import React from 'react';
import { Search, Filter, Plus } from 'lucide-react';
import type { UserFilterOption } from './utils';

interface UserSearchBarProps {
  searchTerm: string;
  filterRole: UserFilterOption;
  totalUsers: number;
  totalFiltered: number;
  startIndex: number;
  endIndex: number;
  onSearchChange: (term: string) => void;
  onFilterChange: (role: UserFilterOption) => void;
  onAddUser: () => void;
}

export const UserSearchBar: React.FC<UserSearchBarProps> = ({
  searchTerm,
  filterRole,
  totalUsers,
  totalFiltered,
  startIndex,
  endIndex,
  onSearchChange,
  onFilterChange,
  onAddUser,
}) => {
  return (
    <>
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
            <p className="text-gray-600">Manage user accounts and permissions</p>
          </div>
          <button onClick={onAddUser} className="btn-primary flex items-center space-x-2">
            <Plus size={20} />
            <span>Add User</span>
          </button>
        </div>
      </div>

      <div className="p-6 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={e => onSearchChange(e.target.value)}
              className="pl-10 input-field w-full"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={filterRole}
              onChange={e => onFilterChange(e.target.value as UserFilterOption)}
              className="input-field"
            >
              <option value="all">All Users</option>
              <option value="admin">Administrators</option>
              <option value="user">Regular Users</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>
          </div>
        </div>

        {totalFiltered > 0 && (
          <div className="mt-4 text-sm text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, totalFiltered)} of {totalFiltered} users
            {(searchTerm || filterRole !== 'all') && (
              <span className="ml-2 text-gray-500">(filtered from {totalUsers} total)</span>
            )}
          </div>
        )}
      </div>
    </>
  );
};
